import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Bot, Check, FileText, Globe2, Landmark, Receipt, Sparkles, Users, Scale, Star } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { landingText } from "@/lib/landingText";
import { formationText } from "@/lib/formationText";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import DemoButton from "./DemoButton";
import ContactDialog from "./ContactDialog";

export const useLT = () => {
  const { lang } = useLang();
  return (k) => landingText[lang][k] ?? landingText.en[k] ?? k;
};

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6, ease: [0.2, 0.7, 0.2, 1] } };

export const Kicker = ({ children }) => (
  <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{children}</p>
);
export const H2 = ({ children }) => (
  <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">{children}</h2>
);
export const Section = ({ id, className = "", children }) => (
  <motion.section id={id} {...fade} className={`mx-auto w-full max-w-6xl px-6 py-24 sm:py-32 ${className}`}>{children}</motion.section>
);

export function Nav() {
  const t = useLT();
  const { lang } = useLang();
  const links = [["#features", "navFeatures"], ["#ai", "navAI"], ["#pricing", "navPricing"], ["#faq", "navFaq"]];
  return (
    <header data-testid="landing-nav" className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#04140B]/80 backdrop-blur-md">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-6">
        <Link to="/" className="flex items-center" data-testid="landing-logo">
          <img src="/images/logo-compact-dark.png" alt="Mohassib" className="h-12 w-auto sm:h-14" />
        </Link>
        <nav className="hidden items-center gap-6 lg:flex">
          {links.map(([h, k]) => (
            <a key={k} href={`/${h}`} data-testid={`nav-${k}`} className="whitespace-nowrap text-sm font-medium text-white/70 transition-colors hover:text-white">{t(k)}</a>
          ))}
          <Link to="/creation-entreprise" data-testid="nav-formation" className="whitespace-nowrap text-sm font-semibold text-[#F0D77A] transition-colors hover:text-white">{formationText[lang]?.navFormation ?? formationText.en.navFormation}</Link>
        </nav>
        <div className="flex items-center gap-2.5">
          <LanguageSwitcher dark />
          <DemoButton variant="nav" />
          <Link data-testid="nav-login-link" to="/login" className="hidden whitespace-nowrap text-sm font-semibold text-white/80 hover:text-white sm:block">{t("navLogin")}</Link>
          <Link data-testid="nav-start-link" to="/register" className="whitespace-nowrap rounded-full bg-[#007A3D] px-4 py-2 text-sm font-bold text-white transition-all hover:bg-[#00934A] hover:shadow-[0_0_24px_rgba(0,196,98,0.35)]">{t("navStart")}</Link>
        </div>
      </div>
    </header>
  );
}

export function Hero() {
  const t = useLT();
  return (
    <section className="relative isolate overflow-hidden pt-20">
      <img src="/images/dubai.jpg" alt="" className="absolute inset-0 -z-20 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#04140B]/60 via-[#04140B]/85 to-[#04140B]" />
      <div className="landing-grain absolute inset-0 -z-10" />
      <div className="mx-auto grid max-w-6xl gap-16 px-6 pb-24 pt-20 sm:pt-28 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:pb-36">
        <div>
          <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3.5 py-1.5 text-xs font-bold tracking-wide text-[#F0D77A]">
            <Sparkles className="h-3.5 w-3.5" />{t("badge")}
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} data-testid="hero-title" className="mt-6 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t("heroTitle1")}<br /><span className="bg-gradient-to-r from-[#00C462] via-[#D4AF37] to-[#00C462] bg-clip-text text-transparent">{t("heroTitle2")}</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">{t("heroText")}</motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link data-testid="hero-cta-register" to="/register" className="group inline-flex h-12 items-center gap-2 rounded-full bg-[#007A3D] px-6 text-sm font-bold text-white transition-all hover:bg-[#00934A] hover:shadow-[0_0_32px_rgba(0,196,98,0.4)]">
              {t("ctaPrimary")}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </Link>
            <DemoButton />
            <a data-testid="hero-cta-features" href="#features" className="inline-flex h-12 items-center rounded-full border border-white/15 px-6 text-sm font-semibold text-white/85 transition-colors hover:border-white/40 hover:bg-white/5">{t("ctaSecondary")}</a>
          </motion.div>
          <p className="mt-4 text-xs text-white/40">{t("noCard")} · {t("demoHint")}</p>
        </div>
        <HeroCard />
      </div>
    </section>
  );
}

function HeroCard() {
  const { lang } = useLang();
  const rows = [["Net sales", 369000], ["Output VAT (5%)", 18450], ["Input VAT", -6120]];
  return (
    <motion.div initial={{ opacity: 0, y: 30, rotate: -1 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ delay: 0.35, duration: 0.8 }} className="relative hidden lg:block">
      <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-[#00C462]/10 blur-3xl" />
      <div data-testid="hero-vat-card" className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/40">VAT 201 · Q2 2026</p>
            <p className="mt-1 font-display text-lg font-bold text-white">Al Noor Trading LLC</p>
          </div>
          <span className="rounded-full border border-[#00C462]/40 bg-[#00C462]/15 px-2.5 py-1 text-[11px] font-bold text-[#7EF0B4]">FTA ready</span>
        </div>
        <div className="mt-6 space-y-3">
          {rows.map(([l, v]) => (
            <div key={l} className="flex items-center justify-between border-b border-white/5 pb-3 text-sm">
              <span className="text-white/60">{l}</span>
              <span className={`font-mono tabular-nums ${v < 0 ? "text-[#FF7B8A]" : "text-white"}`}>{v < 0 ? "−" : ""}AED {Math.abs(v).toLocaleString(lang === "ar" ? "ar-AE" : "en-GB")}</span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <span className="text-sm font-semibold text-white">Net VAT payable</span>
            <span className="font-mono text-xl font-bold tabular-nums text-[#D4AF37]">AED 12,330</span>
          </div>
        </div>
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-3">
          <Bot className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
          <p className="text-xs leading-relaxed text-white/70">Return due 28 July. Box 1b (Dubai) matches 14 posted invoices — nothing to adjust.</p>
        </div>
      </div>
    </motion.div>
  );
}

export function Stats() {
  const t = useLT();
  return (
    <div className="border-y border-white/5 bg-white/[0.02]">
      <div data-testid="stats-strip" className="mx-auto grid max-w-6xl grid-cols-2 divide-white/5 px-6 md:grid-cols-4 md:divide-x rtl:md:divide-x-reverse">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="px-6 py-10">
            <p className="font-display text-3xl font-extrabold text-white sm:text-4xl">{t(`stat${i}v`)}</p>
            <p className="mt-2 text-sm text-white/50">{t(`stat${i}l`)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Logos() {
  const t = useLT();
  const names = ["Al Noor Trading", "Gulf Horizon", "Desert Rose Design", "Emirates Fit Studio", "Marina Tech", "Oasis Consulting"];
  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-white/35">{t("trusted")}</p>
      <div data-testid="logos-strip" className="mt-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-5">
        {names.map((n) => (
          <span key={n} className="font-display text-lg font-bold tracking-tight text-white/30 transition-colors hover:text-white/70">{n}</span>
        ))}
      </div>
    </div>
  );
}

export function Features() {
  const t = useLT();
  const icons = [FileText, Receipt, Landmark, Scale, Users, Globe2];
  return (
    <Section id="features">
      <Kicker>{t("featuresKicker")}</Kicker>
      <H2>{t("featuresTitle")}</H2>
      <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {icons.map((Icon, i) => (
          <div key={i} data-testid={`feature-${i + 1}`} className={`group bg-[#061A0F] p-8 transition-colors hover:bg-[#0A2316] ${i === 1 ? "lg:bg-[#0A2316]" : ""}`}>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#00C462]/30 bg-[#00C462]/10 text-[#00C462] transition-transform group-hover:scale-110">
              <Icon className="h-5 w-5" />
            </div>
            <h3 className="mt-6 font-display text-lg font-bold text-white">{t(`f${i + 1}t`)}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/55">{t(`f${i + 1}d`)}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function AIShowcase() {
  const t = useLT();
  return (
    <Section id="ai" className="grid gap-14 lg:grid-cols-2 lg:items-center">
      <div>
        <Kicker>{t("aiKicker")}</Kicker>
        <H2>{t("aiTitle")}</H2>
        <p className="mt-6 text-base leading-relaxed text-white/60">{t("aiText")}</p>
        <ul className="mt-8 space-y-3">
          {t("aiPoints").map((p) => (
            <li key={p} className="flex items-center gap-3 text-sm text-white/80"><Check className="h-4 w-4 text-[#00C462]" />{p}</li>
          ))}
        </ul>
        <div className="mt-8 inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-amber-500 to-amber-600 px-2.5 py-1 text-xs font-bold text-white shadow-sm"><Sparkles className="h-3.5 w-3.5" />Claude Sonnet 4.5</div>
      </div>
      <div data-testid="ai-demo" className="rounded-2xl border border-white/10 bg-[#061A0F] p-5 shadow-2xl">
        {[1, 2].map((i) => (
          <div key={i} className="space-y-3 py-3">
            <div className="flex justify-end"><p className="max-w-[80%] rounded-2xl rounded-br-sm bg-[#007A3D] px-4 py-2.5 text-sm text-white">{t(`aiQ${i}`)}</p></div>
            <div className="flex items-start gap-3">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#D4AF37]/15 text-[#D4AF37]"><Bot className="h-4 w-4" /></div>
              <p className="max-w-[85%] rounded-2xl rounded-tl-sm border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm leading-relaxed text-white/80">{t(`aiA${i}`)}</p>
            </div>
          </div>
        ))}
        <div className="mt-2 flex items-center gap-2 px-1"><span className="dot" /><span className="dot" /><span className="dot" /></div>
      </div>
    </Section>
  );
}

export function Testimonials() {
  const t = useLT();
  return (
    <Section id="testimonials">
      <Kicker>{t("testiKicker")}</Kicker>
      <H2>{t("testiTitle")}</H2>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <figure key={i} data-testid={`testimonial-${i}`} className="flex flex-col justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-transform hover:-translate-y-1">
            <div>
              <div className="flex gap-1 text-[#D4AF37]">{[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4 fill-current" />)}</div>
              <blockquote className="mt-5 text-base leading-relaxed text-white/80">“{t(`t${i}`)}”</blockquote>
            </div>
            <figcaption className="mt-7 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#007A3D] to-[#D4AF37] font-display text-sm font-bold text-white">{t(`t${i}n`).slice(0, 1)}</div>
              <div><p className="text-sm font-bold text-white">{t(`t${i}n`)}</p><p className="text-xs text-white/50">{t(`t${i}r`)}</p></div>
            </figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
}

export function Pricing() {
  const t = useLT();
  const [contactOpen, setContactOpen] = useState(false);
  const [yearly, setYearly] = useState(false);
  const plans = [[1, 49], [2, 99], [3, 199]];
  return (
    <Section id="pricing">
      <Kicker>{t("pricingKicker")}</Kicker>
      <H2>{t("pricingTitle")}</H2>
      <p className="mt-4 text-sm text-white/50">{t("billing")}</p>
      <div className="mt-8 inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-1 text-sm font-bold">
        <button type="button" data-testid="pricing-monthly" onClick={() => setYearly(false)} className={`rounded-full px-4 py-2 transition-colors ${!yearly ? "bg-[#007A3D] text-white" : "text-white/60"}`}>{t("monthly")}</button>
        <button type="button" data-testid="pricing-yearly" onClick={() => setYearly(true)} className={`flex items-center gap-2 rounded-full px-4 py-2 transition-colors ${yearly ? "bg-[#007A3D] text-white" : "text-white/60"}`}>
          {t("yearly")}<span className="rounded-full bg-[#D4AF37] px-2 py-0.5 text-[10px] font-bold uppercase text-[#1A1400]">{t("twoMonthsFree")}</span>
        </button>
      </div>
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
      <div className="mt-14 grid gap-6 lg:grid-cols-3">
        {plans.map(([i, price]) => {
          const hot = i === 2;
          return (
            <div key={i} data-testid={`plan-${i}`} className={`relative flex flex-col rounded-2xl border p-8 ${hot ? "border-[#D4AF37]/60 bg-gradient-to-b from-[#0E2A19] to-[#061A0F] shadow-[0_0_60px_rgba(212,175,55,0.12)] lg:-translate-y-4" : "border-white/10 bg-white/[0.03]"}`}>
              {hot && <span className="absolute -top-3 start-8 rounded-full bg-[#D4AF37] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#1A1400]">{t("popular")}</span>}
              <h3 className="font-display text-xl font-bold text-white">{t(`p${i}n`)}</h3>
              <p className="mt-1 text-sm text-white/50">{t(`p${i}d`)}</p>
              <p className="mt-6 flex items-baseline gap-1.5">
                <span data-testid={`plan-${i}-price`} className="font-display text-4xl font-extrabold tabular-nums text-white">{yearly ? price * 10 : price} AED</span>
                <span className="text-sm text-white/40">{yearly ? t("perYear") : t("perMonth")}</span>
              </p>
              {yearly && <p className="mt-1 text-xs text-[#F0D77A]">{t("yearlyNote").replace("{m}", price)}</p>}
              <ul className="mt-8 flex-1 space-y-3">
                {t(`p${i}f`).map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/75"><Check className={`mt-0.5 h-4 w-4 shrink-0 ${hot ? "text-[#D4AF37]" : "text-[#00C462]"}`} />{f}</li>
                ))}
              </ul>
              {i === 3 ? (
                <button type="button" data-testid="plan-3-cta" onClick={() => setContactOpen(true)} className="mt-8 inline-flex h-11 items-center justify-center rounded-full border border-white/15 text-sm font-bold text-white transition-all hover:bg-white/5">{t("contact")}</button>
              ) : (
                <Link data-testid={`plan-${i}-cta`} to="/register" className={`mt-8 inline-flex h-11 items-center justify-center rounded-full text-sm font-bold transition-all ${hot ? "bg-[#D4AF37] text-[#1A1400] hover:bg-[#E5C456]" : "border border-white/15 text-white hover:bg-white/5"}`}>
                  {t("choose")}
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

export function Faq() {
  const t = useLT();
  return (
    <Section id="faq" className="max-w-3xl">
      <Kicker>{t("faqKicker")}</Kicker>
      <H2>{t("faqTitle")}</H2>
      <div className="mt-12 divide-y divide-white/10 border-y border-white/10">
        {[1, 2, 3, 4].map((i) => (
          <details key={i} data-testid={`faq-${i}`} className="group py-5">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold text-white">
              {t(`q${i}`)}
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/15 text-white/60 transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-white/60">{t(`a${i}`)}</p>
          </details>
        ))}
      </div>
    </Section>
  );
}

export function FinalCta() {
  const t = useLT();
  return (
    <div className="mx-auto max-w-6xl px-6 pb-24">
      <motion.div {...fade} data-testid="final-cta" className="relative overflow-hidden rounded-3xl border border-[#00C462]/20 bg-gradient-to-br from-[#0A3A1F] via-[#06251A] to-[#04140B] px-8 py-16 text-center sm:px-16">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#00C462]/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <h2 className="relative font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">{t("finalTitle")}</h2>
        <p className="relative mx-auto mt-5 max-w-xl text-base text-white/65">{t("finalText")}</p>
        <Link data-testid="final-cta-register" to="/register" className="relative mt-10 inline-flex h-12 items-center gap-2 rounded-full bg-[#007A3D] px-7 text-sm font-bold text-white transition-all hover:bg-[#00934A] hover:shadow-[0_0_32px_rgba(0,196,98,0.4)]">
          {t("ctaPrimary")}<ArrowRight className="h-4 w-4 rtl:rotate-180" />
        </Link>
      </motion.div>
    </div>
  );
}

export function Footer() {
  const t = useLT();
  const { lang } = useLang();
  const hrefs = ["/#features", "/#pricing", "/#faq", "/login", "/creation-entreprise"];
  const links = [...t("footerLinks"), formationText[lang]?.navFormation ?? formationText.en.navFormation];
  return (
    <footer data-testid="landing-footer" className="border-t border-white/5">
      <div className="h-1 w-full bg-gradient-to-r from-[#007A3D] via-white to-[#CE1126]" />
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-6 py-10 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <img src="/images/logo-dark.png" alt="Mohassib" className="h-14 w-auto" />
          <p className="text-xs text-white/40">{t("footerTag")}</p>
        </div>
        <nav className="flex flex-wrap gap-6">
          {links.map((l, i) => hrefs[i].startsWith("/#")
            ? <a key={l} href={hrefs[i]} className="text-sm text-white/55 hover:text-white">{l}</a>
            : <Link key={l} to={hrefs[i]} className="text-sm text-white/55 hover:text-white">{l}</Link>)}
        </nav>
        <p className="text-xs text-white/35">© {new Date().getFullYear()} Mohassib · {t("footerRights")}</p>
      </div>
    </footer>
  );
}
